import type { BookingEntry } from '@nono/contract';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '@/components/ui/badge';
import { formatBookedAt, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';

/*
 * Click-to-open, full-size booking drill-down for the Overview "Bookinger" KPI.
 * A centered dialog (portaled to <body>) — replaces the cramped hover popover so
 * the data has room to breathe: one generous card per booking with who, when,
 * member/sale ids, and the exact ad + campaign + how the ad↔booking link was
 * established (session-proven vs heuristic). Escape / backdrop / × close; body
 * scroll locked while open; the list scrolls inside a fixed-height panel.
 */

const MESSAGES = {
  nb: {
    title: 'Bookinger',
    close: 'Lukk',
    realInternal: (real: string, internal: string) => `${real} reelle · ${internal} interne`,
    total: (n: string) => `${n} totalt`,
    empty: 'Ingen bookinger i perioden.',
    memberSale: (memberId: number, saleId: number) => `Medlem #${memberId} · Salg #${saleId}`,
    saleOnly: (saleId: number) => `Salg #${saleId}`,
    internal: 'Intern test',
    noAd: 'Ingen annonsekobling',
    campaign: 'Kampanje',
    sessionProven: '✓ Bekreftet i kundens egen økt — presis kobling',
    heuristic: 'Heuristisk kobling (siste uparede annonseklikk) — Meta er fasit',
    heuristicFoot:
      'Kobling annonse–booking er vår heuristikk der den er merket som det — Metas tall er Metas egne.',
  },
  en: {
    title: 'Bookings',
    close: 'Close',
    realInternal: (real: string, internal: string) => `${real} real · ${internal} internal`,
    total: (n: string) => `${n} total`,
    empty: 'No bookings in the period.',
    memberSale: (memberId: number, saleId: number) => `Member #${memberId} · Sale #${saleId}`,
    saleOnly: (saleId: number) => `Sale #${saleId}`,
    internal: 'Internal test',
    noAd: 'No ad link',
    campaign: 'Campaign',
    sessionProven: '✓ Confirmed in the customer’s own session — precise link',
    heuristic: 'Heuristic link (most recent unclaimed ad click) — Meta is the source of truth',
    heuristicFoot:
      "Ad–booking pairing is our heuristic where labelled as such — Meta's numbers are Meta's own.",
  },
};
type Msgs = (typeof MESSAGES)['nb'];

export function BookingsModal({
  open,
  onClose,
  bookings,
  rangeLabel,
  counts,
}: {
  open: boolean;
  onClose: () => void;
  bookings: BookingEntry[];
  rangeLabel: string;
  counts: { total: number; internal: number } | null;
}) {
  const m = useViewMessages(MESSAGES);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const real = counts ? counts.total - counts.internal : bookings.length;
  const sub =
    counts && counts.internal > 0
      ? m.realInternal(formatMetric(real, 'int'), formatMetric(counts.internal, 'int'))
      : m.total(formatMetric(counts ? counts.total : bookings.length, 'int'));

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4">
      {/* backdrop */}
      <button
        type="button"
        aria-label={m.close}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/50 backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={m.title}
        className="relative flex max-h-[90vh] w-full max-w-[680px] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl sm:max-h-[85vh]"
      >
        {/* header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-4 sm:px-6 sm:py-5">
          <div>
            <h2 className="text-title text-fg">{m.title}</h2>
            <p className="mt-1 text-label text-fg-muted">
              {rangeLabel} · {sub}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={m.close}
            className="grid size-10 shrink-0 place-items-center rounded-lg border border-border text-fg-muted hover:bg-surface-2 hover:text-fg sm:size-9"
          >
            ✕
          </button>
        </div>

        {/* list */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {bookings.length === 0 ? (
            <p className="px-2 py-12 text-center text-body text-fg-muted">{m.empty}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {bookings.map((b) => (
                <BookingCard key={`${b.saleId}-${b.bookedAt}-${b.name}`} b={b} m={m} />
              ))}
            </ul>
          )}
        </div>

        {/* footer */}
        <p className="border-t border-border px-4 py-3 text-micro leading-relaxed text-fg-subtle sm:px-6">
          {m.heuristicFoot}
        </p>
      </div>
    </div>,
    document.body,
  );
}

function BookingCard({ b, m }: { b: BookingEntry; m: Msgs }) {
  const ts = formatBookedAt(b.bookedAt);
  const sp = ts.lastIndexOf(' ');
  const datePart = sp > 0 ? ts.slice(0, sp) : ts;
  const clockPart = sp > 0 ? ts.slice(sp + 1) : null;
  const adLabel = b.ad ? (b.ad.adName ?? b.ad.adId ?? null) : null;

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-border/60 bg-surface-2/40 px-4 py-3.5 sm:flex-row sm:items-start sm:gap-4">
      <div className="flex shrink-0 flex-row gap-1.5 leading-tight sm:w-14 sm:flex-col sm:gap-0">
        <span className="text-label tabular font-medium text-fg-muted">{datePart}</span>
        {clockPart ? <span className="text-micro tabular text-fg-subtle">{clockPart}</span> : null}
      </div>

      <div className="min-w-0 flex-1">
        <span className="block truncate text-body font-semibold text-fg">{b.name}</span>
        <span className="block truncate text-micro text-fg-subtle">
          {b.memberId !== null ? m.memberSale(b.memberId, b.saleId) : m.saleOnly(b.saleId)}
        </span>
        {b.internal ? (
          <span className="mt-1.5 inline-block">
            <Badge tone="warn">{m.internal}</Badge>
          </span>
        ) : null}
      </div>

      <div className="flex w-full flex-col items-start gap-1 text-left sm:max-w-[44%] sm:shrink-0 sm:items-end sm:text-right">
        {adLabel ? (
          <>
            <Badge tone="booking">
              <span className="truncate">{adLabel}</span>
              {b.ad?.method === 'session' ? <span aria-hidden> ✓</span> : null}
            </Badge>
            {b.ad?.campaign ? (
              <span className="line-clamp-2 text-micro text-fg-subtle">
                {m.campaign}: {b.ad.campaign}
              </span>
            ) : null}
            <span
              className={`text-micro ${b.ad?.method === 'session' ? 'text-good-fg' : 'text-fg-subtle'}`}
            >
              {b.ad?.method === 'session' ? m.sessionProven : m.heuristic}
            </span>
          </>
        ) : (
          <span className="text-micro text-fg-subtle">{m.noAd}</span>
        )}
      </div>
    </li>
  );
}
